import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../api/client';

interface Region { id: number; name: string; }
interface NewsItem { id: number; title: string; description: string; imageS3: string | null; createdAt: string; regionId: number | null; }

export default function NewsPage() {
  const { t, i18n } = useTranslation();
  const [regions, setRegions] = useState<Region[]>([]);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const locale = i18n.language === 'en' ? 'en-US' : i18n.language === 'ky' ? 'ky-KG' : 'ru-RU';

  useEffect(() => { axios.get(`${API_BASE}/regions`).then(r => setRegions(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const url = selectedRegion ? `${API_BASE}/news/region/${selectedRegion}` : `${API_BASE}/news`;
    axios.get(url).then(r => setNews(r.data)).catch(() => setNews([])).finally(() => setLoading(false));
  }, [selectedRegion, i18n.language]);

  const regionName = (id: number | null) => regions.find(r => r.id === id)?.name || t('news_general');

  return (
    <div style={s.page}>
      <h1 style={s.pageTitle}>{t('news_page_title')}</h1>
      <div style={s.filterBar}>
        <button style={{ ...s.filterBtn, ...(selectedRegion === null ? s.filterBtnActive : {}) }} onClick={() => setSelectedRegion(null)}>
          {t('news_all_regions')}
        </button>
        {regions.map(r => (
          <button key={r.id} style={{ ...s.filterBtn, ...(selectedRegion === r.id ? s.filterBtnActive : {}) }} onClick={() => setSelectedRegion(r.id)}>
            {r.name}
          </button>
        ))}
      </div>
      {loading ? (
        <div style={s.empty}>{t('news_page_loading')}</div>
      ) : news.length === 0 ? (
        <div style={s.empty}>{t('news_page_empty')}</div>
      ) : (
        <div style={s.grid}>
          {news.map(item => (
            <div key={item.id} style={{ ...s.card, cursor: 'pointer' }} onClick={() => navigate(`/news/${item.id}`)}>
              {item.imageS3 && <img src={`${API_BASE}/news/media/${item.imageS3}`} alt={item.title} style={s.cardImg} />}
              <div style={s.cardBody}>
                <div style={s.cardMeta}>
                  <span style={s.cardRegion}>📍 {regionName(item.regionId)}</span>
                  <span style={s.cardDate}>{item.createdAt ? new Date(item.createdAt).toLocaleDateString(locale) : ''}</span>
                </div>
                <h3 style={s.cardTitle}>{item.title}</h3>
                <p style={s.cardDesc}>{item.description?.slice(0, 120)}{item.description?.length > 120 ? '...' : ''}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1100, margin: '0 auto', padding: '48px 40px' },
  pageTitle: { fontSize: '2rem', color: '#1a3a6b', marginBottom: 28 },
  filterBar: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 36 },
  filterBtn: { padding: '8px 18px', borderRadius: 20, border: '2px solid #1a3a6b', background: 'transparent', color: '#1a3a6b', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' },
  filterBtnActive: { background: '#1a3a6b', color: '#fff' },
  empty: { textAlign: 'center', padding: 60, color: '#888', fontSize: '1rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 24 },
  card: { background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  cardImg: { width: '100%', height: 180, objectFit: 'cover' },
  cardBody: { padding: '18px 20px' },
  cardMeta: { display: 'flex', justifyContent: 'space-between', marginBottom: 10 },
  cardRegion: { fontSize: '0.8rem', color: '#1a3a6b', fontWeight: 600 },
  cardDate: { fontSize: '0.8rem', color: '#999' },
  cardTitle: { fontSize: '1rem', fontWeight: 700, color: '#1a2a4a', marginBottom: 10, lineHeight: 1.4 },
  cardDesc: { fontSize: '0.88rem', color: '#555', lineHeight: 1.6 },
};
